import React, { useCallback, useState } from "react";
import { useAuth } from "../auth/useAuth";
import { useEffectiveUnits } from "../units/useEffectiveUnits";
import type { Lang, LocalizedText } from "./i18n";
import { hasTranslationKey, pickLang, textFor, translateKey } from "./i18n";
import { I18nContext } from "./i18nContext";
import { UiLanguageProvider } from "../../../../../../shared/frontend/ui/uiText";
import { LANGUAGE_DE, LANGUAGE_EN } from "../../../../../../shared/frontend/i18n/languageCodes";

function getInitialStoredLang(): Lang {
  const s = typeof localStorage !== "undefined" ? localStorage.getItem("language") : null;
  if (s === LANGUAGE_DE || s === LANGUAGE_EN) return s;
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  return nav && nav.toLowerCase().startsWith("de") ? "de" : "en";
}

const StoredLanguageContext = React.createContext<[Lang, (lang: Lang) => void] | null>(null);

export function StoredLanguageProvider(props: { children: React.ReactNode }) {
  const [stored, setStored] = useState<Lang>(getInitialStoredLang);
  const setter = useCallback((lang: Lang) => {
    if (typeof localStorage !== "undefined") localStorage.setItem("language", lang);
    setStored(lang);
  }, []);
  return (
    <StoredLanguageContext.Provider value={[stored, setter]}>
      {props.children}
    </StoredLanguageContext.Provider>
  );
}

export function useStoredLanguage(): [Lang, (lang: Lang) => void] {
  const ctx = React.useContext(StoredLanguageContext);
  if (!ctx) throw new Error("useStoredLanguage must be used within StoredLanguageProvider.");
  return ctx;
}

export type I18nContextValue = {
  lang: Lang;
  /** Translation helper for i18n keys or explicit localized objects. */
  text: (value: LocalizedText) => string;
  /**
   * Translation helper for keys.
   *
   * Convention: The key is English.
   * - In DE: returns mapped translation if available, otherwise the key
   * - In EN: returns mapped translation or the key
   */
  tr: (key: string) => string;
};


export function I18nProvider(props: { children: React.ReactNode; lang?: Lang }) {
  const auth = useAuth();
  const [storedLang] = useStoredLanguage();
  const units = useEffectiveUnits();

  const computedLang = React.useMemo(() => {
    const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
    return pickLang({
      unitsLocale: units.locale,
      userLanguage: auth.info?.language || null,
      storedLanguage: storedLang,
      browserLanguage
    });
  }, [auth.info?.language, storedLang, units.locale]);

  const lang = props.lang || computedLang;

  const value = React.useMemo<I18nContextValue>(() => {
    const missing = new Set<string>();
    const warnMissing = (key: string) => {
      if (!hasTranslationKey(key) && typeof console !== "undefined" && !missing.has(key)) {
        missing.add(key);
        console.warn(`[i18n] Missing translation for: "${key}"`);
      }
    };
    return {
      lang,
      text: (v) => {
        if (typeof v === "string") warnMissing(v);
        return textFor(lang, v);
      },
      tr: (key: string) => {
        const translated = translateKey(lang, key);
        if (translated !== key) return translated;
        warnMissing(key);
        return key;
      }
    };
  }, [lang]);

  return (
    <I18nContext.Provider value={value}>
      <UiLanguageProvider value={lang}>{props.children}</UiLanguageProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n muss innerhalb von I18nProvider verwendet werden.");
  return ctx;
}

